/**
 * useReactivePropertySync - Syncs reactive properties with remote updates via Pusher
 *
 * This hook:
 * 1. Listens for Pusher events when properties are updated by other users
 * 2. Updates the local reactive properties accordingly
 * 3. Provides a method to broadcast local property changes to other clients
 */

import { useEffect, useRef, useCallback, useContext } from 'react';
import { subscribeToPrivateProjectChannel, unsubscribeFromPrivateProjectChannel, clientId } from '../../../hooks/pusher';
import { hasGlobalPropertyService, getGlobalPropertyService, setGlobalBroadcastFunction, setGlobalBroadcastBatchFunction } from './globalPropertyService';
import { PropertyType, PropertyValueData, PropertyValueType } from '../types';
import type { PropertyUpdate } from '@pointorama/potree-shared';
import type { Channel } from 'pusher-js';
import { UserContext } from '../../../contexts/UserContext';

/** Event payload for single property update */
interface PropertyUpdatedEvent {
  identifier: string;
  value: PropertyValueData;
  valueType: PropertyValueType;
  propertyType: string;
  propertyIdentifier: string;
  updatedBy: string;
  /** User ID of the user who made the change — used to filter user-scoped properties */
  userId?: string;
}

/** Event payload for batch property updates */
interface PropertiesBatchUpdatedEvent {
  /** Legacy format: one entry per property per object (used by server-triggered events) */
  updates?: Array<{
    identifier: string;
    value: PropertyValueData;
    valueType: PropertyValueType;
    propertyType: string;
    propertyIdentifier: string;
  }>;
  /** Compact format: grouped by identifier+value, with objectIdentifiers list (used by client-triggered events) */
  groups?: Array<{
    identifier: string;
    value: PropertyValueData;
    propertyType: string;
    objectIdentifiers: string[];
  }>;
  updatedBy: string;
  userId?: string;
}

/** Map server property type strings to client PropertyType enum */
function mapPropertyType(serverType: string): PropertyType {
  switch (serverType) {
    case 'STATE_BY_USER':
      return PropertyType.PROJECT_USER;
    case 'GLOBAL_STATE':
      return PropertyType.PROJECT_GLOBAL;
    case 'ORGANIZATION':
      return PropertyType.ORGANIZATION;
    case 'USER':
      return PropertyType.USER_SETTINGS;
    default:
      return PropertyType.PROJECT_GLOBAL;
  }
}

/** Map client PropertyType enum to server property type strings */
function mapPropertyTypeToServer(propertyType: PropertyType): string {
  switch (propertyType) {
    case PropertyType.PROJECT_USER:
      return 'STATE_BY_USER';
    case PropertyType.PROJECT_GLOBAL:
      return 'GLOBAL_STATE';
    case PropertyType.ORGANIZATION:
      return 'ORGANIZATION';
    case PropertyType.USER_SETTINGS:
      return 'USER';
    default:
      return 'GLOBAL_STATE';
  }
}

export interface UseReactivePropertySyncOptions {
  projectId: string;
  /** Called when a remote property update is received */
  onRemoteUpdate?: (identifier: string, objectIdentifier: string, value: PropertyValueData) => void;
}

export interface UseReactivePropertySyncReturn {
  /** Broadcast a property update to other clients via Pusher (no database save) */
  broadcastPropertyUpdate: (identifier: string, objectIdentifier: string, propertyType: PropertyType, value: PropertyValueData) => void;
}

/**
 * Hook that syncs reactive properties with remote Pusher updates
 * Call this once in your app (e.g., alongside useRealtimeUpdates)
 *
 * Uses private channels for client-triggered events, enabling immediate
 * real-time updates without going through the server for each change.
 */
export const useReactivePropertySync = (options: UseReactivePropertySyncOptions): UseReactivePropertySyncReturn => {
  const { projectId, onRemoteUpdate } = options;
  const currentUser = useContext(UserContext);
  const currentUserId = currentUser?.id;
  const channelRef = useRef<Channel | null>(null);
  const onRemoteUpdateRef = useRef(onRemoteUpdate);

  useEffect(() => {
    onRemoteUpdateRef.current = onRemoteUpdate;
  });

  useEffect(() => {
    if (!projectId) return;

    // Subscribe to private channel for client-triggered events
    const channel = subscribeToPrivateProjectChannel(projectId);
    channelRef.current = channel;

    /**
     * Handle single property update from remote (both server-triggered and client-triggered)
     */
    const handlePropertyUpdated = (event: PropertyUpdatedEvent) => {
      // Ignore our own updates (using unique client ID per browser session)
      if (event.updatedBy === clientId) return;
      if (!hasGlobalPropertyService()) return;

      const propertyType = mapPropertyType(event.propertyType);

      // User properties should only be applied by the same user (cross-tab sync)
      if (propertyType === PropertyType.PROJECT_USER || propertyType === PropertyType.USER_SETTINGS) {
        if (!event.userId || event.userId !== currentUserId) return;
      }

      const service = getGlobalPropertyService();
      if (service.hasProperty(event.identifier, event.propertyIdentifier, propertyType)) {
        service.setValueFromRemote(event.identifier, event.propertyIdentifier, propertyType, event.value);
        onRemoteUpdateRef.current?.(event.identifier, event.propertyIdentifier, event.value);
      }
    };

    /**
     * Handle batch property updates from remote
     */
    const handlePropertiesBatchUpdated = (event: PropertiesBatchUpdatedEvent) => {
      if (event.updatedBy === clientId) return;
      if (!hasGlobalPropertyService()) return;

      const service = getGlobalPropertyService();

      // Handle compact grouped format (client-triggered events)
      if (event.groups) {
        for (const group of event.groups) {
          const propertyType = mapPropertyType(group.propertyType);

          if (propertyType === PropertyType.PROJECT_USER || propertyType === PropertyType.USER_SETTINGS) {
            if (!event.userId || event.userId !== currentUserId) continue;
          }

          for (const objectId of group.objectIdentifiers) {
            if (service.hasProperty(group.identifier, objectId, propertyType)) {
              service.setValueFromRemote(group.identifier, objectId, propertyType, group.value);
              onRemoteUpdateRef.current?.(group.identifier, objectId, group.value);
            }
          }
        }
        return;
      }

      // Handle legacy per-update format (server-triggered events)
      if (event.updates) {
        for (const update of event.updates) {
          const propertyType = mapPropertyType(update.propertyType);

          if (propertyType === PropertyType.PROJECT_USER || propertyType === PropertyType.USER_SETTINGS) {
            if (!event.userId || event.userId !== currentUserId) continue;
          }

          if (service.hasProperty(update.identifier, update.propertyIdentifier, propertyType)) {
            service.setValueFromRemote(update.identifier, update.propertyIdentifier, propertyType, update.value);
            onRemoteUpdateRef.current?.(update.identifier, update.propertyIdentifier, update.value);
          }
        }
      }
    };

    // Bind only client-triggered events for real-time sync between browser sessions.
    // Server-triggered events ('property-updated', 'properties-batch-updated') are NOT bound
    // because they echo our own mutations back and cause bounceback flicker.
    // Initial load comes from the DB query; cross-client sync uses Pusher client events.
    channel.bind('client-property-updated', handlePropertyUpdated);
    channel.bind('client-properties-batch-updated', handlePropertiesBatchUpdated);

    return () => {
      channel.unbind('client-property-updated', handlePropertyUpdated);
      channel.unbind('client-properties-batch-updated', handlePropertiesBatchUpdated);

      // Unsubscribe from private channel
      unsubscribeFromPrivateProjectChannel(projectId);
      channelRef.current = null;
    };
  }, [projectId, currentUserId]);

  /**
   * Broadcast a single property update to other clients via Pusher client event.
   * User properties include userId so only the same user's other tabs apply them.
   */
  const broadcastPropertyUpdate = useCallback((identifier: string, objectIdentifier: string, propertyType: PropertyType, value: PropertyValueData) => {
    const channel = channelRef.current;
    if (!channel) return;

    channel.trigger('client-property-updated', {
      identifier,
      value,
      propertyType: mapPropertyTypeToServer(propertyType),
      propertyIdentifier: objectIdentifier,
      updatedBy: clientId,
      userId: currentUserId,
    });
  }, [currentUserId]);

  /**
   * Broadcast a batch of property updates in a single Pusher event.
   * Used by PropertyService.batchUpdate() for group property panel changes.
   */
  const broadcastPropertyUpdateBatch = useCallback((updates: PropertyUpdate[]) => {
    const channel = channelRef.current;
    if (!channel || updates.length === 0) return;

    // Group updates that share the same identifier+value+propertyType into compact events.
    // Instead of sending 600 copies of { identifier, value, propertyIdentifier },
    // send one { identifier, value, propertyType, objectIdentifiers: [...600 ids] }.
    const groupKey = (u: PropertyUpdate) => `${u.identifier}:${u.propertyType}:${JSON.stringify(u.value)}`;
    const grouped = new Map<string, { identifier: string; value: PropertyValueData; propertyType: PropertyType; objectIdentifiers: string[] }>();

    for (const u of updates) {
      const key = groupKey(u);
      const existing = grouped.get(key);
      if (existing) {
        existing.objectIdentifiers.push(u.objectIdentifier);
      } else {
        grouped.set(key, { identifier: u.identifier, value: u.value, propertyType: u.propertyType, objectIdentifiers: [u.objectIdentifier] });
      }
    }

    channel.trigger('client-properties-batch-updated', {
      groups: Array.from(grouped.values()).map((g) => ({
        identifier: g.identifier,
        value: g.value,
        propertyType: mapPropertyTypeToServer(g.propertyType),
        objectIdentifiers: g.objectIdentifiers,
      })),
      updatedBy: clientId,
      userId: currentUserId,
    });
  }, [currentUserId]);

  // Register broadcast functions globally so PropertyService can use them
  useEffect(() => {
    setGlobalBroadcastFunction(broadcastPropertyUpdate);
    setGlobalBroadcastBatchFunction(broadcastPropertyUpdateBatch);

    return () => {
      setGlobalBroadcastFunction(null);
      setGlobalBroadcastBatchFunction(null);
    };
  }, [broadcastPropertyUpdate, broadcastPropertyUpdateBatch]);

  return { broadcastPropertyUpdate };
};
